/**
 * CommentsModal — view and add comments on a post.
 * Uses existing public.comments table.
 */
import { useState, useEffect, useRef } from 'react';
import {
  View, Text, StyleSheet, Modal, FlatList,
  TextInput, TouchableOpacity, KeyboardAvoidingView,
  Platform, ActivityIndicator, Pressable,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { X, Send } from 'lucide-react-native';
import { RADIUS, FONT, SPACE } from '@/constants/designTokens';
import { TYPO } from '@/constants/typography';

interface Comment {
  id: string;
  content: string;
  created_at: string;
  user_id: string;
  display_name: string | null;
  handle: string | null;
}

interface Props {
  visible: boolean;
  postId: string;
  postAuthorName?: string;
  onClose: () => void;
  onCommentAdded?: () => void;
}

export default function CommentsModal({ visible, postId, postAuthorName, onClose, onCommentAdded }: Props) {
  const { theme } = useTheme();
  const { profile } = useAuth();
  const c = theme.colors;
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef<TextInput>(null);

  useEffect(() => {
    if (visible && postId) loadComments();
  }, [visible, postId]);

  const loadComments = async () => {
    setLoading(true);
    try {
      const { data } = await supabase
        .from('comments')
        .select('id, content, created_at, user_id, profiles!comments_user_id_fkey(display_name, handle)')
        .eq('post_id', postId)
        .order('created_at', { ascending: true })
        .limit(100);

      if (data) {
        setComments(data.map((c: any) => ({
          id: c.id,
          content: c.content,
          created_at: c.created_at,
          user_id: c.user_id,
          display_name: c.profiles?.display_name ?? null,
          handle: c.profiles?.handle ?? null,
        })));
      }
    } catch { /* non-blocking */ }
    setLoading(false);
  };

  const handleSubmit = async () => {
    const trimmed = text.trim();
    if (!trimmed || !profile?.id || submitting) return;
    setSubmitting(true);
    try {
      const { data, error } = await supabase.from('comments').insert({
        post_id: postId,
        user_id: profile.id,
        content: trimmed,
      }).select('id, content, created_at, user_id').single();

      if (!error && data) {
        setComments(prev => [...prev, {
          ...data,
          display_name: profile.display_name ?? null,
          handle: profile.handle ?? null,
        }]);
        setText('');
        onCommentAdded?.();
      }
    } catch { /* non-blocking */ }
    setSubmitting(false);
  };

  const formatTime = (iso: string) => {
    const d = new Date(iso);
    const now = new Date();
    const diff = (now.getTime() - d.getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  return (
    <Modal visible={visible} animationType="slide" presentationStyle="pageSheet" onRequestClose={onClose}>
      <SafeAreaView style={{ flex: 1, backgroundColor: c.background }} edges={['top']}>
        <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : 'height'} style={{ flex: 1 }}>
          {/* Header */}
          <View style={[styles.header, { borderBottomColor: c.border }]}>
            <Text style={[styles.title, { color: c.text, fontFamily: TYPO.heading.bold }]}>
              {postAuthorName ? `${postAuthorName}'s Post` : 'Comments'}
            </Text>
            <Pressable onPress={onClose} hitSlop={12} accessibilityRole="button" accessibilityLabel="Close comments">
              <X size={22} color={c.textSecondary} />
            </Pressable>
          </View>

          {/* Comment list */}
          {loading ? (
            <ActivityIndicator color={c.accent} style={{ marginTop: 40 }} />
          ) : (
            <FlatList
              data={comments}
              keyExtractor={item => item.id}
              keyboardShouldPersistTaps="handled"
              contentContainerStyle={styles.list}
              ListEmptyComponent={
                <Text style={[styles.empty, { color: c.textTertiary }]}>
                  No comments yet. Be the first.
                </Text>
              }
              renderItem={({ item }) => (
                <View style={[styles.commentRow, { borderBottomColor: c.border + '40' }]}>
                  <View style={[styles.avatar, { backgroundColor: c.surfaceVariant }]}>
                    <Text style={[styles.avatarText, { color: c.textSecondary }]}>
                      {(item.display_name || item.handle || '?').charAt(0).toUpperCase()}
                    </Text>
                  </View>
                  <View style={{ flex: 1 }}>
                    <View style={styles.commentMeta}>
                      <Text style={[styles.commentName, { color: c.text }]}>
                        {item.display_name || `@${item.handle}` || 'Lifter'}
                      </Text>
                      <Text style={[styles.commentTime, { color: c.textTertiary }]}>
                        {formatTime(item.created_at)}
                      </Text>
                    </View>
                    <Text style={[styles.commentText, { color: c.textSecondary }]}>{item.content}</Text>
                  </View>
                </View>
              )}
            />
          )}

          {/* Input */}
          <View style={[styles.inputRow, { borderTopColor: c.border, backgroundColor: c.surface }]}>
            <TextInput
              ref={inputRef}
              style={[styles.input, { backgroundColor: c.surfaceVariant, color: c.text, borderColor: c.border }]}
              placeholder="Add a comment..."
              placeholderTextColor={c.textTertiary}
              value={text}
              onChangeText={setText}
              multiline
              maxLength={500}
              returnKeyType="send"
              onSubmitEditing={handleSubmit}
            />
            <Pressable
              style={[styles.sendBtn, { backgroundColor: text.trim() ? c.accent : c.surfaceVariant }]}
              onPress={handleSubmit}
              disabled={!text.trim() || submitting}
              android_ripple={{ color: 'rgba(0,0,0,0.15)', borderless: false }}
              accessibilityRole="button"
              accessibilityLabel="Send comment"
            >
              {submitting ? (
                <ActivityIndicator size="small" color={c.accentForeground} />
              ) : (
                <Send size={16} color={text.trim() ? c.accentForeground : c.textTertiary} />
              )}
            </Pressable>
          </View>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </Modal>
  );
}

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: SPACE.lg,
    paddingVertical: 14,
    borderBottomWidth: 1,
  },
  title: { fontSize: FONT.base, textTransform: 'uppercase', letterSpacing: 0.5 },
  list: { padding: SPACE.base, gap: 12, flexGrow: 1 },
  empty: { textAlign: 'center', fontSize: FONT.sm, marginTop: 40 },
  commentRow: {
    flexDirection: 'row',
    gap: 10,
    paddingBottom: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarText: { fontSize: 13, fontWeight: '700' },
  commentMeta: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 2 },
  commentName: { fontSize: FONT.sm, fontWeight: '700' },
  commentTime: { fontSize: 11 },
  commentText: { fontSize: FONT.sm, lineHeight: 18 },
  inputRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 8,
    padding: 12,
    borderTopWidth: 1,
  },
  input: {
    flex: 1,
    borderWidth: 1,
    borderRadius: RADIUS.xs,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: FONT.sm,
    maxHeight: 100,
  },
  sendBtn: {
    width: 40,
    height: 40,
    borderRadius: RADIUS.xs,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
